import * as React from "react";
import type { GraphAuth } from "@usable/office-embed";
import { OFFICE_ACCENT as ACCENT } from "../theme";

/**
 * GraphConnect — the "Connect Microsoft 365" affordance, rendered as a section in
 * the add-in menu drawer (HistoryPanel). Host-agnostic and opt-in: any app that
 * bootstraps a `graph` capability gets it; the shell passes the built GraphAuth in.
 *
 * Graph tools acquire their token silently on the agent path and never pop UI; the
 * ONE place interactive consent is granted is the Connect button here, because a
 * click is the user gesture browsers require to allow the MSAL popup. We request
 * the UNION of scopes so the user consents once (e.g. Mail.Read + Calendars.Read),
 * after which every per-tool silent acquire is invisible.
 *
 * The token is used in-pane by the tool handlers and never crosses the chat bridge;
 * this component only drives connect/disconnect and reflects status. Theming mirrors
 * the drawer: CSS system colors adapt to Office light/dark, Office accent as highlight.
 */

type Status = "checking" | "unsupported" | "disconnected" | "connected";

interface GraphConnectProps {
  graphAuth: GraphAuth;
  /** Union of scopes consented at connect-time (e.g. Mail.Read + Calendars.Read). */
  scopes: string[];
}

export function GraphConnect({ graphAuth, scopes }: GraphConnectProps): React.ReactElement {
  const [status, setStatus] = React.useState<Status>("checking");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // On mount: feature-detect NAA, then probe for an existing silent token so a
  // previously-connected user shows "connected" without any prompt. Any silent
  // failure (no account / interaction required) just means "not connected yet".
  React.useEffect(() => {
    let cancelled = false;
    if (!graphAuth.isNaaSupported()) {
      setStatus("unsupported");
      return;
    }
    void graphAuth
      .acquireGraphToken(scopes)
      .then(() => {
        if (!cancelled) setStatus("connected");
      })
      .catch(() => {
        if (!cancelled) setStatus("disconnected");
      });
    return () => {
      cancelled = true;
    };
  }, [graphAuth, scopes]);

  const connect = React.useCallback(() => {
    setBusy(true);
    setError(null);
    // Interactive path — the click IS the required user gesture for the popup.
    void graphAuth
      .acquireGraphToken(scopes, { interactive: true })
      .then(() => setStatus("connected"))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Couldn't connect to Microsoft 365.");
      })
      .finally(() => setBusy(false));
  }, [graphAuth, scopes]);

  const disconnect = React.useCallback(() => {
    setBusy(true);
    setError(null);
    void graphAuth
      .disconnect()
      .then(() => setStatus("disconnected"))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Couldn't disconnect.");
      })
      .finally(() => setBusy(false));
  }, [graphAuth]);

  const connected = status === "connected";

  return (
    <div className="uxg-sec">
      <style>{styles}</style>

      {status === "checking" && <span className="uxg-note">Microsoft 365 — checking…</span>}

      {status === "unsupported" && (
        <span className="uxg-note">Microsoft 365 isn’t available in this version of Outlook.</span>
      )}

      {status === "disconnected" && (
        <>
          <div className="uxg-row">
            <span className="uxg-lbl">
              <span className="uxg-dot" aria-hidden="true" /> Microsoft 365 — not connected
            </span>
            <button type="button" className="uxg-btn uxg-btn-primary" disabled={busy} onClick={connect}>
              {busy ? "Connecting…" : "Connect"}
            </button>
          </div>
          <span className="uxg-hint">
            Lets the assistant read across your mail and calendar (“summarise my recent emails”,
            “what’s on my calendar this week”). Content you ask about is processed by Usable’s AI.
          </span>
        </>
      )}

      {connected && (
        <>
          <div className="uxg-row">
            <span className="uxg-lbl">
              <span className="uxg-dot uxg-dot-on" aria-hidden="true" /> Microsoft 365 — connected
            </span>
            <button type="button" className="uxg-btn" disabled={busy} onClick={disconnect}>
              {busy ? "Disconnecting…" : "Disconnect"}
            </button>
          </div>
        </>
      )}

      {error && <span className="uxg-err">{error}</span>}
    </div>
  );
}

const styles = `
.uxg-sec {
  flex: 0 0 auto;
  padding: 10px 12px 10px 16px;
  border-top: 1px solid rgba(128,128,128,0.22);
  font: 12px "Segoe UI", system-ui, sans-serif;
  color: CanvasText;
}
.uxg-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.uxg-lbl { display: inline-flex; align-items: center; gap: 6px; opacity: 0.85; min-width: 0; }
.uxg-dot { width: 8px; height: 8px; border-radius: 50%; background: GrayText; flex: none; }
.uxg-dot-on { background: #2ea043; }
.uxg-btn {
  flex: 0 0 auto;
  font: 600 12px "Segoe UI", system-ui, sans-serif; color: CanvasText;
  background: rgba(128,128,128,0.1);
  border: 1px solid rgba(128,128,128,0.42); border-radius: 6px;
  padding: 4px 12px; cursor: pointer;
  transition: background .12s, border-color .12s, filter .12s;
}
.uxg-btn:hover:not(:disabled) { background: rgba(128,128,128,0.2); border-color: rgba(128,128,128,0.6); }
.uxg-btn:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: 2px; }
.uxg-btn:disabled { opacity: 0.6; cursor: default; }
.uxg-btn-primary { color: #fff; background: ${ACCENT}; border-color: ${ACCENT}; }
.uxg-btn-primary:hover:not(:disabled) { filter: brightness(1.08); background: ${ACCENT}; }
.uxg-hint { display: block; margin-top: 6px; color: GrayText; line-height: 1.4; }
.uxg-err { display: block; margin-top: 6px; color: #d13438; }
.uxg-note { color: GrayText; }
`;
